const SITE_NAME = "FionaZiya";
const DEFAULT_DESCRIPTION = "Discover handcrafted and occasion-ready styles from FionaZiya.";
const DEFAULT_IMAGE_PATH = "https://cdn.mansercommerce.com/media/fionaziya.JPG";
const JSON_LD_ID = "storefront-product-jsonld";

const getOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return "https://fionaziya.in";
};

const toAbsoluteUrl = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    return new URL(raw, getOrigin()).toString();
  } catch {
    return "";
  }
};

const findMeta = (attr: "name" | "property", key: string): HTMLMetaElement | null => {
  if (typeof document === "undefined") return null;

  return (
    Array.from(document.head.querySelectorAll("meta")).find(
      (node) => node.getAttribute(attr) === key
    ) ?? null
  );
};

const upsertMeta = (attr: "name" | "property", key: string, content?: string | null) => {
  if (typeof document === "undefined") return;

  const value = String(content || "").trim();
  const existing = findMeta(attr, key);

  if (!value) {
    existing?.remove();
    return;
  }

  const meta = existing ?? document.createElement("meta");
  meta.setAttribute(attr, key);
  meta.setAttribute("content", value);

  if (!existing) {
    document.head.appendChild(meta);
  }
};

const upsertCanonical = (href?: string | null) => {
  if (typeof document === "undefined") return;

  const value = String(href || "").trim();
  const existing = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!value) {
    existing?.remove();
    return;
  }

  const link = existing ?? document.createElement("link");
  link.setAttribute("rel", "canonical");
  link.setAttribute("href", value);

  if (!existing) {
    document.head.appendChild(link);
  }
};

const upsertJsonLd = (payload?: Record<string, unknown> | null) => {
  if (typeof document === "undefined") return;

  const existing = document.getElementById(JSON_LD_ID);
  if (!payload) {
    existing?.remove();
    return;
  }

  const script = existing ?? document.createElement("script");
  script.id = JSON_LD_ID;
  script.setAttribute("type", "application/ld+json");
  script.textContent = JSON.stringify(payload).replace(/</g, "\\u003c");

  if (!existing) {
    document.head.appendChild(script);
  }
};

type StorefrontSeoPayload = {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  canonicalUrl?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  jsonLd?: Record<string, unknown> | null;
};

export const applyStorefrontSeo = ({
  title,
  description,
  keywords,
  canonicalUrl,
  imageUrl,
  imageAlt,
  jsonLd,
}: StorefrontSeoPayload) => {
  if (typeof document === "undefined") return;

  const finalTitle = String(title || "").trim() || SITE_NAME;
  const finalDescription = String(description || "").trim() || DEFAULT_DESCRIPTION;
  const finalKeywords = String(keywords || "").trim();
  const finalCanonical = String(canonicalUrl || "").trim();
  const finalImageUrl = toAbsoluteUrl(imageUrl || DEFAULT_IMAGE_PATH);
  const finalImageAlt = String(imageAlt || finalTitle || SITE_NAME).trim() || SITE_NAME;

  document.title = finalTitle;

  upsertMeta("name", "description", finalDescription);
  upsertMeta("name", "keywords", finalKeywords || null);
  upsertCanonical(finalCanonical || null);

  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:type", "product");
  upsertMeta("property", "og:title", finalTitle);
  upsertMeta("property", "og:description", finalDescription);
  upsertMeta("property", "og:url", finalCanonical || null);
  upsertMeta("property", "og:image", finalImageUrl || null);
  upsertMeta("property", "og:image:url", finalImageUrl || null);
  upsertMeta("property", "og:image:secure_url", finalImageUrl || null);
  upsertMeta("property", "og:image:alt", finalImageAlt || null);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", finalTitle);
  upsertMeta("name", "twitter:description", finalDescription);
  upsertMeta("name", "twitter:image", finalImageUrl || null);
  upsertMeta("name", "twitter:image:alt", finalImageAlt || null);

  upsertJsonLd(jsonLd ?? null);
};

export const restoreStorefrontSeoDefaults = () => {
  const origin = getOrigin().replace(/\/+$/, "");
  const defaultImageUrl = toAbsoluteUrl(DEFAULT_IMAGE_PATH);

  applyStorefrontSeo({
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    keywords: null,
    canonicalUrl: null,
    imageUrl: defaultImageUrl,
    imageAlt: SITE_NAME,
    jsonLd: null,
  });

  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", `${origin}/`);
};
